import React, { useEffect, useState } from "react";
import { ProSidebar, Menu, MenuItem } from "react-pro-sidebar";
import "react-pro-sidebar/dist/css/styles.css";
import { Box, Typography, useTheme } from "@mui/material";
import { Link, useLocation } from "react-router-dom";
import {
  Cpu,
  Drawer,
  IpAddressTag,
  Suitcase,
  ViewStructureUp,
  Settings,
  ArrowEnlargeTag,
  CloudUpload,
  ProfileCircle,
  UserStar,
  UserCrown,
} from "iconoir-react";
import { Link as IconoirLink } from "iconoir-react";
import { useAuth } from "app/authHooks";
import { hasPermission } from "core/utils/roles";

type ItemProps = {
  title: string;
  uniqueTitle: string;
  to?: string;
  url?: string;
  external?: boolean;
  icon: React.ReactNode;
  selected: string;
  setSelected: React.Dispatch<React.SetStateAction<string>>;
};

const Item: React.FC<ItemProps> = ({
  title,
  uniqueTitle,
  to,
  url,
  external,
  icon,
  selected,
  setSelected,
}) => {
  const theme = useTheme();
  const colors = theme.palette;

  const handleClick = () => {
    setSelected(uniqueTitle);
    if (external && url) {
      window.open(url, "_blank", "noopener,noreferrer");
    }
  };

  return (
    <MenuItem
      active={selected === uniqueTitle}
      style={
        selected === uniqueTitle
          ? {
            color: colors.primary.main,
            backgroundColor: colors.primary.light,
          }
          : { color: colors.grey[500] }
      }
      onClick={handleClick}
      icon={icon}
      className="menuItem"
    >
      <Typography
        style={{
          fontFamily: "Lato, sans-serif",
          fontWeight: selected === uniqueTitle ? 700 : 500,
          color: selected === uniqueTitle ? colors.primary.main : colors.grey[500],
        }}
      >
        {title}
      </Typography>
      {!external && to && <Link to={to} />}
    </MenuItem>
  );
};

const Sidebar = () => {
  const { attributes } = useAuth();
  // const role = attributes["custom:role"] as Role;

  const theme = useTheme();
  const location = useLocation();
  const colors = theme.palette;
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [selected, setSelected] = useState("dashboard");
  const props = { selected, setSelected };

  const syncSelectedWithRoute = () => {
    const path = location.pathname;
    const routeMap: { [key: string]: string } = {
      "/": "dashboard",
      "/events": "events",
      "/jobs": "jobs",
      "/state-machines": "machines",
      "/ip-set": "ip-whitelists",
      "/io-config": "io-app-config",
      "/integrations": "intelliflo",
      "/investor-users": "investor-users",
      "/adviser-users": "adviser-users",
      "/entity-mapping": "entity-mapping",
      "/document-category": "document-category",
      "/onboarding/csv-to-json": "csv-to-json",
      "/security/roles": "roles",
      "/security/permissions": "permissions",
      "/investor-aml/review-list": "investor-review",
      "/investor-aml/uplift-tracker/uplift-list": "uplift-list",
    };

    // List of external link unique titles that should not be reset by route sync
    const externalLinkTitles = ["wl-allure-report"];

    // Don't reset selected state if current selection is an external link
    if (externalLinkTitles.includes(selected)) {
      return;
    }

    let matchedRoute = "/";
    for (const route in routeMap) {
      if (path.startsWith(route) && route.length > matchedRoute.length) {
        matchedRoute = route;
      }
    }
    if (matchedRoute) {
      setSelected(routeMap[matchedRoute]);
    }
  };

  useEffect(() => {
    syncSelectedWithRoute();
  }, [location.pathname]);

  return (
    <Box
      sx={{
        "& .pro-sidebar-inner": {
          background: `${colors.cardBackground} !important`,
          borderRight: `1px solid ${colors.cardBorderColor}`,
        },
        "&  .pro-icon-wrapper": {
          backgroundColor: "transparent !important",
        },
        "& .pro-inner-item": {
          padding: "5px 35px 5px 20px !important",
        },
        "& .pro-inner-item:hover": {
          backgroundColor: `${colors.primary.light} !important`,
          color: `${colors.primary.main} !important`,
        },
        "& .pro-menu-item.active": {
          backgroundColor: `${colors.primary.light} !important`,
          "& .pro-inner-item": {
            color: `${colors.primary.main} !important`,
            backgroundColor: `${colors.primary.light} !important`,
          },
          "& .MuiTypography-root": {
            color: `${colors.primary.main} !important`,
            fontWeight: "700 !important",
          },
        },
        "& .menu-header:hover": {
          backgroundColor: `${colors.grey[900]} !important`,
        },
        "& .pro-menu": {
          overflowY: "hidden",
          scrollbarGutter: "stable",
          "-ms-overflow-style": "none",
          "&::-webkit-scrollbar": {
            opacity: 0,
          },
          transition: "all 0.8s ease-in-out",
        },
        "& .pro-menu:hover": {
          overflowY: "auto",
          scrollbarWidth: "thin",
          "&::-webkit-scrollbar": {
            opacity: 1,
          },
        },
        boxShadow:
          "4px 8px 16px rgba(64,110,184,0.16), 0px 0px 8px rgba(64,110,184,0.04)",
        zIndex: "2",
      }}
    >
      <ProSidebar>
        <Box
          display="flex"
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
          style={{
            maxHeight: "20rem",
            maxWidth: "20rem",
            marginTop: "1.5rem",
            marginBottom: 30,
          }}
        >
          <Box
            component="img"
            alt="ss&c-logo"
            src="../../../assets/images/ssc-hubwise-logo.svg"
            sx={{
              width: 208,
              height: "auto",
            }}
          />
          <Typography
            variant="h5"
            sx={{
              fontWeight: 700,
              color: colors.blueAccent[400],
              marginTop: 2,
              letterSpacing: 2,
              fontSize: 16,
            }}
          >
            ADMIN PLATFORM
          </Typography>
        </Box>
        <Menu>
          <Box paddingLeft="5%">
            {hasPermission("view:dashboard") && (
              <Item
                title="Dashboard"
                uniqueTitle="dashboard"
                to="/"
                icon={<ViewStructureUp />}
                {...props}
              />
            )}
            <Typography
              variant={isCollapsed ? "h6" : "h5"}
              color={colors.grey[500]}
              sx={{ m: "15px 0px 5px 20px", fontWeight: 600 }}
            >
              Data
            </Typography>
            {hasPermission("view:events") && (
              <Item
                title="Events"
                uniqueTitle="events"
                to="/events"
                icon={<Drawer />}
                {...props}
              />
            )}
            <Typography
              variant={isCollapsed ? "h6" : "h5"}
              color={colors.grey[500]}
              sx={{ m: "15px 0px 5px 20px", fontWeight: 600 }}
            >
              System
            </Typography>
            {hasPermission("view:jobs") && (
              <Item
                title="Jobs"
                uniqueTitle="jobs"
                to="/jobs"
                icon={<Suitcase />}
                {...props}
              />
            )}
            {hasPermission("view:machines") && (
              <Item
                title="Machines"
                uniqueTitle="machines"
                to="/state-machines"
                icon={<Cpu />}
                {...props}
              />
            )}
            {hasPermission("view:ip-whitelists") && (
              <Item
                title="IP Whitelists"
                uniqueTitle="ip-whitelists"
                to="/ip-set"
                icon={<IpAddressTag />}
                {...props}
              />
            )}
            <Typography
              variant={isCollapsed ? "h6" : "h5"}
              color={colors.grey[500]}
              sx={{ m: "15px 0px 5px 20px", fontWeight: 600 }}
            >
              Integration
            </Typography>
            {hasPermission("view:io-app-config") && (
              <Item
                title="IO App Config"
                uniqueTitle="io-app-config"
                to="/io-config"
                icon={<Settings />}
                {...props}
              />
            )}
            {hasPermission("view:io-integrations") && (
              <Item
                title="Intelliflo"
                uniqueTitle="intelliflo"
                to="/integrations"
                icon={<ArrowEnlargeTag />}
                {...props}
              />
            )}
            {hasPermission("view:entity-mapping") && (
              <Item
                title="Entity Mapping"
                uniqueTitle="entity-mapping"
                to="/entity-mapping"
                icon={<IconoirLink />}
                {...props}
              />
            )}
            {hasPermission("view:document-category") && (
              <Item
                title="Document Category"
                uniqueTitle="document-category"
                to="/document-category"
                icon={<Drawer />}
                {...props}
              />
            )}
            <Typography
              variant={isCollapsed ? "h6" : "h5"}
              color={colors.grey[500]}
              sx={{ m: "15px 0px 5px 20px", fontWeight: 600 }}
            >
              Onboarding
            </Typography>
            {hasPermission("view:portal-onboarding") && (
              <Item
                title="Portal Onboarding"
                uniqueTitle="csv-to-json"
                to="/onboarding/csv-to-json"
                icon={<CloudUpload />}
                {...props}
              />
            )}
            {hasPermission("view:investor-onboarding") && (
              <Item
                title="Investor Onboarding"
                uniqueTitle="investor-users"
                to="/investor-users"
                icon={<ProfileCircle />}
                {...props}
              />
            )}
            {hasPermission("view:advisor-onboarding") && (
              <Item
                title="Adviser Onboarding"
                uniqueTitle="adviser-users"
                to="/adviser-users"
                icon={<UserStar />}
                {...props}
              />
            )}
            {hasPermission("view:admin-onboarding") && (
              <Item
                title="Admin Onboarding"
                uniqueTitle="admin-users"
                to="/admin-users"
                icon={<UserStar />}
                {...props}
              />
            )}
            <Typography
              variant={isCollapsed ? "h6" : "h5"}
              color={colors.grey[500]}
              sx={{ m: "15px 0px 5px 20px", fontWeight: 600 }}
            >
              AML
            </Typography>
            {hasPermission("view:investor-aml") && (
              <Item
                title="Investor Review"
                uniqueTitle="investor-review"
                to="/investor-aml/review-list"
                icon={<UserStar />}
                {...props}
              />
            )}
            {hasPermission("view:uplift-list") && (
              <Item
                title="Uplift Tracker"
                uniqueTitle="uplift-list"
                to="/investor-aml/uplift-tracker/uplift-list"
                icon={<UserStar />}
                {...props}
              />
            )}
            <Typography
              variant={isCollapsed ? "h6" : "h5"}
              color={colors.grey[500]}
              sx={{ m: "15px 0px 5px 20px", fontWeight: 600 }}
            >
              Security
            </Typography>
            {hasPermission("view:roles") && (
              <Item
                title="Roles"
                uniqueTitle="roles"
                to="/security/roles"
                icon={<UserCrown />}
                {...props}
              />
            )}
            {hasPermission("view:permissions") && (
              <Item
                title="Permissions"
                uniqueTitle="permissions"
                to="/security/permissions"
                icon={<UserCrown />}
                {...props}
              />
            )}
            <Typography
              variant={isCollapsed ? "h6" : "h5"}
              color={colors.grey[500]}
              sx={{ m: "15px 0px 5px 20px", fontWeight: 600 }}
            >
              Report
            </Typography>
            {hasPermission("view:reports") && (
              <Item
                title="Allure Report"
                uniqueTitle="wl-allure-report"
                url={process.env.REACT_APP_ALLURE_REPORT_URL}
                external={true}
                icon={<Drawer />}
                {...props}
              />
            )}
          </Box>
        </Menu>
      </ProSidebar>
    </Box>
  );
};

export default Sidebar;
